import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type PopoverPosition = { top: number; left: number; width: number };

/**
 * Anchors a portaled popover under a trigger element. Positions in viewport
 * coordinates (the popover is `position: fixed`), flips above the trigger when
 * there is not enough room below, and aligns to the trigger's *start* edge so
 * RTL layouts read correctly. Closes on outside click, scroll and resize.
 */
export function usePopover({
  open,
  onClose,
  minWidth = true,
  gap = 6,
  estimatedHeight = 288,
}: {
  open: boolean;
  onClose: () => void;
  /** Force the popover to be at least as wide as the trigger. */
  minWidth?: boolean;
  gap?: number;
  estimatedHeight?: number;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const height = popoverRef.current?.offsetHeight || estimatedHeight;
    const below = window.innerHeight - rect.bottom - gap;
    const flip = below < height && rect.top - gap > below;
    const top = flip ? Math.max(gap, rect.top - gap - height) : rect.bottom + gap;

    const width = minWidth ? rect.width : 0;
    const popWidth = popoverRef.current?.offsetWidth || rect.width;
    const isRTL = getComputedStyle(trigger).direction === 'rtl';
    // Align to the trigger's start edge, then clamp inside the viewport.
    const raw = isRTL ? rect.right - Math.max(popWidth, width) : rect.left;
    const left = Math.min(Math.max(gap, raw), Math.max(gap, window.innerWidth - popWidth - gap));

    setPosition({ top, left, width });
  }, [estimatedHeight, gap, minWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    measure();
    // A second pass once the popover has rendered and its real size is known.
    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => onClose();
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [open, onClose]);

  return { triggerRef, popoverRef, position, measure };
}

/** Inline style for a portaled popover positioned by `usePopover`. */
export function popoverStyle(position: PopoverPosition | null) {
  return {
    position: 'fixed' as const,
    top: position?.top ?? -9999,
    left: position?.left ?? -9999,
    minWidth: position?.width || undefined,
    visibility: position ? ('visible' as const) : ('hidden' as const),
    zIndex: 60,
  };
}
